import { FiArrowLeft, FiBookmark, FiShare2, FiEye, FiX, FiPlay, FiPause, FiMoreVertical, FiMoreHorizontal } from "react-icons/fi";
import { RiFontSize } from "react-icons/ri";
import { MdFileDownload } from "react-icons/md";
import { FaFacebook, FaFacebookMessenger, FaXTwitter, FaWhatsapp, FaTelegram } from "react-icons/fa6";
import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import "./usercss/articlepage.css";
import LearnSpeechControlBar from "./LearnSpeechControlBar";

import { db } from "../../firebase";
import { doc, getDoc, onSnapshot, collection, getDocs, query, where, limit } from "firebase/firestore";
import { publishedService, statsService, userService, bookmarkService } from "../../services/firestoreService";
import { auth } from "../../firebase";
import { createPDF } from "../../utils/pdfExport";
import logoAuraVit from "../../images/auravit_logo.png";
import cfsiLogo from "../../images/CFSI-mobile-logo-sr.png";
import pdfLogo from "../../images/pdf logo.png";

export default function LearnArticle() {
    const navigate = useNavigate();
    const { id } = useParams();
    const [article, setArticle] = useState(null);
    const [backendBodyHtml, setBackendBodyHtml] = useState("");
    const [bookmarked, setBookmarked] = useState(false);
    const [shareModal, setShareModal] = useState(false);
    const [copyButtonText, setCopyButtonText] = useState("Copy Link");
    const [articleStats, setArticleStats] = useState({ views: 0, shares: 0, bookmarks: 0 });
    const [isPlaying, setIsPlaying] = useState(false);
    const [showSpeechControl, setShowSpeechControl] = useState(false);
    const [speechCurrentTime, setSpeechCurrentTime] = useState(0);
    const [speechDuration, setSpeechDuration] = useState(0);
    const [playbackRate, setPlaybackRate] = useState(1);
    const [currentUser, setCurrentUser] = useState(null);
    const [fontSize, setFontSize] = useState(16); // Default font size in px
    const [showFontModal, setShowFontModal] = useState(false);
    const [showDropdown, setShowDropdown] = useState(false);
    const [relatedArticles, setRelatedArticles] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [articleNotFound, setArticleNotFound] = useState(false);
    
    const bookmarkListenerRef = useRef(null);
    const speechRef = useRef(null);
    const speechStartTimeRef = useRef(0);
    const lastArticleSnapshotRef = useRef(null);
    const unsubscribeArticleListenerRef = useRef(null);
    const publishedDocRef = useRef(null);
    const userDataCacheRef = useRef(null);
    const statsListenerRef = useRef(null);

    // Helper function to get related articles by category (optimized query)
    const getRelatedArticlesByCategory = useCallback(async (category, currentContentId) => {
        try {
            const q = query(
                collection(db, 'published'),
                where('category', '==', category),
                limit(5) // Fetch 5 to account for excluding current article
            );
            const snapshot = await getDocs(q);
            const articles = snapshot.docs
                .map(doc => ({ id: doc.id, ...doc.data() }))
                .filter(article => article.contentId !== currentContentId)
                .slice(0, 4); // Limit to 4 related articles
            return articles;
        } catch (error) {
            console.error('LearnArticle: Error fetching related articles by category:', error);
            return [];
        }
    }, []);

    const getArticleUrl = () => {
        if (!article) return "";
        return `https://auravit-cfsi.com/learn/article/${article.id}`;
    };

    // Fetch stats for current article
    const fetchArticleStats = useCallback(async (articleId) => {
        try {
            const stats = await statsService.getStats(articleId);
            setArticleStats(stats);
        } catch (error) {
            console.error('LearnArticle: Error fetching stats:', error);
            setArticleStats({ views: 0, shares: 0, bookmarks: 0 });
        }
    }, []);

    // Helper function to calculate read time based on word count
    const calculateReadTime = (content) => {
        if (!content || typeof content !== 'string') {
            return 1; // Default to 1 minute for empty/invalid content
        }

        // Strip HTML tags if content contains HTML
        const textContent = content.replace(/<[^>]+>/g, '');
        
        // Trim and split by whitespace to count words
        const words = textContent.trim().split(/\s+/);
        const wordCount = words.length;
        
        // Calculate read time using 200 words per minute, always round up
        const readTime = Math.ceil(wordCount / 200);
        
        // Minimum 1 minute
        return Math.max(1, readTime);
    };

    // Helper function to get read time for current article
    const getArticleReadTime = () => {
        if (!article) return 1;
        
        // Use stored read time if available
        if (article.readTime) {
            return article.readTime;
        }
        
        // Try to calculate from backendBodyHtml if available
        if (backendBodyHtml) {
            return calculateReadTime(backendBodyHtml);
        }
        
        // Fallback to 1 minute for articles without content
        return 1;
    };

    // Helper function to format date
    const formatDate = (dateValue) => {
        if (!dateValue) return "March 2, 2024"; // Default fallback
        
        // Handle Firestore timestamp objects
        let date;
        if (dateValue.toDate && typeof dateValue.toDate === 'function') {
            // Firestore timestamp object
            date = dateValue.toDate();
        } else if (dateValue instanceof Date) {
            // Already a Date object
            date = dateValue;
        } else if (typeof dateValue === 'string') {
            // Try to parse as string
            date = new Date(dateValue);
        } else {
            // Try to parse as timestamp
            date = new Date(dateValue);
        }
        
        // Check if date is valid
        if (isNaN(date.getTime())) {
            return "March 2, 2024"; // Fallback for invalid dates
        }
        
        // Format as "Month Day, Year"
        return date.toLocaleDateString('en-US', { 
            year: 'numeric', 
            month: 'long', 
            day: 'numeric' 
        });
    };

    // Helper function to get article date
    const getArticleDate = () => {
        if (!article) return "March 2, 2024";
        
        // Try approvedAt first (for published content)
        if (article.approvedAt) {
            return formatDate(article.approvedAt);
        }
        
        // Try publishedAt as fallback
        if (article.publishedAt) {
            return formatDate(article.publishedAt);
        }
        
        // Try createdAt as another fallback
        if (article.createdAt) {
            return formatDate(article.createdAt);
        }
        
        // Default fallback
        return "March 2, 2024";
    };

    // Setup bookmark listener function - optimized to query single document
    const setupBookmarkListener = async (contentId) => {
        const user = auth.currentUser;
        
        if (user) {
            // Check initial bookmark status from local cache first
            const bookmarks = bookmarkService.getLocalBookmarks();
            const bookmarked = bookmarks[contentId] === true;
            setBookmarked(bookmarked);
            
            // Set up real-time listener on single bookmark document
            if (bookmarkListenerRef.current) {
                bookmarkListenerRef.current();
            }
            
            const bookmarkRef = doc(db, 'users', user.uid, 'bookmarks', contentId);
            bookmarkListenerRef.current = onSnapshot(bookmarkRef, (docSnapshot) => {
                if (docSnapshot.exists()) {
                    const data = docSnapshot.data();
                    const isCurrentlyBookmarked = data.bookmarked === true;
                    
                    setBookmarked(isCurrentlyBookmarked);
                    
                    // Update local cache
                    const currentBookmarks = bookmarkService.getLocalBookmarks();
                    bookmarkService.setLocalBookmarks({
                        ...currentBookmarks,
                        [contentId]: isCurrentlyBookmarked
                    });
                } else {
                    // Document doesn't exist means not bookmarked
                    setBookmarked(false);
                    
                    // Update local cache
                    const currentBookmarks = bookmarkService.getLocalBookmarks();
                    const updatedBookmarks = { ...currentBookmarks };
                    delete updatedBookmarks[contentId];
                    bookmarkService.setLocalBookmarks(updatedBookmarks);
                }
            }, (error) => {
                console.error('LearnArticle: Error in real-time bookmark listener:', error);
            });
        } else {
            setBookmarked(false);
        }
    };

    const speechIntervalRef = useRef(null);
    const seekPositionRef = useRef(0);

    useEffect(() => {
        const user = auth.currentUser;
        if (!user) {
            userDataCacheRef.current = null;
            return;
        }
        let active = true;
        getDoc(doc(db, 'users', user.uid)).then((snap) => {
            if (active && snap.exists()) {
                userDataCacheRef.current = snap.data();
            }
        }).catch(() => {});
        return () => { active = false; };
    }, [auth.currentUser?.uid]);

    useEffect(() => {
        try {
            const referrer = document.referrer || '';
            const prevPath = window.location.pathname;
            let origin = 'learn-hub';
            if (referrer.includes('/learn')) origin = 'learn-hub';
            else if (referrer.includes('/user-home')) origin = 'user-home';
            sessionStorage.setItem('auravit:article-origin', origin);
            sessionStorage.setItem('auravit:article-entry', prevPath);
            // Also track article-origin
            window.history.replaceState(
                { ...(window.history.state || {}), auravitArticleBackGuard: true },
                document.title
            );
        } catch (e) {}
    }, []);

    useEffect(() => {
        const isNumeric = /^\d+$/.test(String(id || ""));

        const cleanupListeners = () => {
            // Cleanup article data refresh interval
            if (unsubscribeArticleListenerRef.current) {
                unsubscribeArticleListenerRef.current();
                unsubscribeArticleListenerRef.current = null;
            }
            // Cleanup bookmark listener
            if (bookmarkListenerRef.current) {
                bookmarkListenerRef.current();
                bookmarkListenerRef.current = null;
            }
            // Cleanup stats listener
            if (statsListenerRef.current) {
                statsListenerRef.current();
                statsListenerRef.current = null;
            }
            lastArticleSnapshotRef.current = null;
            publishedDocRef.current = null;
        };

        setIsLoading(true);
        setArticleNotFound(false);

        if (!isNumeric) {
            (async () => {
                try {
                    const publishedContent = await publishedService.getPublishedByContentId(String(id));
                    if (publishedContent) {
                        publishedDocRef.current = publishedContent;
                        
                        // Function to fetch article data (one-time, not real-time)
                        const fetchArticleData = async () => {
                            try {
                                const snap = await getDoc(doc(db, "published", publishedContent.id));
                                if (snap.exists()) {
                                    const updatedPublishedContent = snap.data();
                                    const currentSnapshot = JSON.stringify({
                                        title: updatedPublishedContent.title,
                                        socialTitle: updatedPublishedContent.socialTitle,
                                        socialDescription: updatedPublishedContent.socialDescription,
                                        subtitle: updatedPublishedContent.subtitle,
                                        socialImageUrl: updatedPublishedContent.socialImageUrl,
                                        category: updatedPublishedContent.category,
                                        readTime: updatedPublishedContent.readTime,
                                        approvedAt: updatedPublishedContent.approvedAt,
                                        publishedAt: updatedPublishedContent.publishedAt,
                                        bodyHtml: updatedPublishedContent.bodyHtml,
                                        stats: updatedPublishedContent.stats
                                    });

                                    if (lastArticleSnapshotRef.current === currentSnapshot) {
                                        return;
                                    }

                                    lastArticleSnapshotRef.current = currentSnapshot;
                                    publishedDocRef.current = { id: snap.id, ...updatedPublishedContent };

                                    const articleFromDb = {
                                        id: updatedPublishedContent.contentId,
                                        title: updatedPublishedContent.title || updatedPublishedContent.socialTitle,
                                        description: updatedPublishedContent.socialDescription || updatedPublishedContent.subtitle || "",
                                        img: updatedPublishedContent.socialImageUrl,
                                        category: updatedPublishedContent.category,
                                        readTime: updatedPublishedContent.readTime,
                                        approvedAt: updatedPublishedContent.approvedAt,
                                        publishedAt: updatedPublishedContent.publishedAt,
                                    };
                                    setArticle(articleFromDb);
                                    setBackendBodyHtml(updatedPublishedContent.bodyHtml || "");

                                    // Set up real-time stats listener
                                    if (statsListenerRef.current) {
                                        statsListenerRef.current();
                                    }
                                    const publishedRef = doc(db, 'published', updatedPublishedContent.contentId);
                                    statsListenerRef.current = onSnapshot(publishedRef, (docSnapshot) => {
                                        if (docSnapshot.exists() && docSnapshot.data()?.stats) {
                                            setArticleStats(docSnapshot.data().stats);
                                        }
                                    });
                                }
                            } catch (error) {
                                console.error('LearnArticle: Error fetching article data:', error);
                            }
                        };
                        
                        // Initial fetch
                        await fetchArticleData();

                        // Set up periodic refresh every 5 minutes (300000ms)
                        // This prevents the write amplification loop from real-time listeners
                        const refreshInterval = setInterval(() => {
                            fetchArticleData();
                        }, 300000); // 5 minutes

                        unsubscribeArticleListenerRef.current = () => {
                            clearInterval(refreshInterval);
                            if (statsListenerRef.current) {
                                statsListenerRef.current();
                                statsListenerRef.current = null;
                            }
                        };

                        const articleFromDb = {
                            id: publishedContent.contentId,
                            title: publishedContent.title || publishedContent.socialTitle,
                            description: publishedContent.socialDescription || publishedContent.subtitle || "",
                            img: publishedContent.socialImageUrl,
                            category: publishedContent.category,
                            readTime: publishedContent.readTime,
                            approvedAt: publishedContent.approvedAt,
                            publishedAt: publishedContent.publishedAt,
                        };
                        setArticle(articleFromDb);
                        setBackendBodyHtml(publishedContent.bodyHtml || "");

                        // Always fetch latest stats from stats service instead of using cached stats
                        await fetchArticleStats(publishedContent.contentId);

                        const related = await getRelatedArticlesByCategory(publishedContent.category, publishedContent.contentId);
                        setRelatedArticles(related);

                        await setupBookmarkListener(publishedContent.contentId);
                        setIsLoading(false);
                        return;
                    } else {
                        const ref = doc(db, "contents", String(id));
                        const snap = await getDoc(ref);
                        if (snap.exists()) {
                            const c = snap.data() || {};
                            const articleFromDb = {
                                id: snap.id,
                                title: c.title || c.socialTitle,
                                description: c.socialDescription || c.subtitle || "",
                                img: c.socialImageUrl,
                                category: c.category,
                                readTime: c.readTime || calculateReadTime(c.bodyHtml),
                                approvedAt: c.approvedAt,
                                publishedAt: c.publishedAt,
                                createdAt: c.createdAt,
                            };
                            setArticle(articleFromDb);
                            setBackendBodyHtml(c.bodyHtml || "");

                            // Always fetch latest stats from stats service
                            await fetchArticleStats(snap.id);

                            // Set up real-time stats listener for contents collection
                            if (statsListenerRef.current) {
                                statsListenerRef.current();
                            }
                            const contentRef = doc(db, 'contents', snap.id);
                            statsListenerRef.current = onSnapshot(contentRef, (docSnapshot) => {
                                if (docSnapshot.exists() && docSnapshot.data()?.stats) {
                                    setArticleStats(docSnapshot.data().stats);
                                }
                            });

                            const related = await getRelatedArticlesByCategory(c.category, snap.id);
                            setRelatedArticles(related);

                            await setupBookmarkListener(snap.id);
                            setIsLoading(false);
                            return;
                        }
                    }
                } catch (e) {
                    console.error('LearnArticle: Error fetching content:', e);
                }
                setArticle(null);
                setArticleNotFound(true);
                setIsLoading(false);
            })();
        } else {
            setArticle(null);
            setArticleNotFound(true);
            setIsLoading(false);
        }

        return cleanupListeners;
    }, [id, getRelatedArticlesByCategory, fetchArticleStats]);

    useEffect(() => {
        if (article) {
            const currentUrl = getArticleUrl();
            document.title = article.title || "AuraVit";
            
            const updateMetaTag = (property, content) => {
                let tag = document.querySelector(`meta[property="${property}"]`);
                if (!tag) {
                    tag = document.createElement('meta');
                    tag.setAttribute('property', property);
                    document.head.appendChild(tag);
                }
                tag.setAttribute('content', content);
            };

            const updateNameMetaTag = (name, content) => {
                let tag = document.querySelector(`meta[name="${name}"]`);
                if (!tag) {
                    tag = document.createElement('meta');
                    tag.setAttribute('name', name);
                    document.head.appendChild(tag);
                }
                tag.setAttribute('content', content);
            };

            updateMetaTag('og:title', article.title || "");
            updateMetaTag('og:description', article.description || "");
            updateMetaTag('og:image', article.img || "");
            updateMetaTag('og:url', currentUrl);
            updateMetaTag('og:type', 'article');

            updateNameMetaTag('description', article.description || "");
            updateNameMetaTag('twitter:card', 'summary_large_image');
            updateNameMetaTag('twitter:title', article.title || "");
            updateNameMetaTag('twitter:description', article.description || "");
            updateNameMetaTag('twitter:image', article.img || "");
        }
    }, [article]);

    // Handle click outside to close dropdown
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (!event.target.closest('.article-dropdown-container')) {
                setShowDropdown(false);
            }
        };

        window.addEventListener('mousedown', handleClickOutside);

        return () => {
            window.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    const handleBack = () => {
        if (window.history.length > 2) {
            navigate(-1);
        } else if (id) {
            const prev = sessionStorage.getItem('auravit:article-origin');
            if (prev === 'learn-hub') {
                navigate('/learn');
            } else {
                navigate('/learn');
            }
        } else {
            navigate('/learn');
        }
    };

    const handleBookmark = async () => {
        const articleId = article.id;
        const user = auth.currentUser;

        if (!user) return;

        try {
            const articleTitle = article.title || article.socialTitle || "Untitled Article";
            const newBookmarkState = await bookmarkService.toggleBookmark(user.uid, articleId, articleTitle);
            setBookmarked(newBookmarkState);

            const opts = {
                cachedUserData: userDataCacheRef.current,
                cachedPublishedDoc: publishedDocRef.current
            };
            if (newBookmarkState) {
                await statsService.incrementBookmarks(articleId, user.uid, opts);
            } else {
                await statsService.decrementBookmarks(articleId, user.uid, opts);
            }
            // Real-time listener will update the stats automatically
        } catch (error) {
            console.error('LearnArticle: Error toggling bookmark:', error);
        }
    };

    const handleShare = async () => {
        // Only open the modal - don't increment shares yet
        // Shares will be incremented when user actually clicks a platform button
        setShareModal(true);
        setCopyButtonText("Copy Link");
    };

    const handleCloseShareModal = () => {
        setShareModal(false);
    };

    const handleCopyLink = async () => {
        const articleId = article.id;
        const user = auth.currentUser;

        // Increment shares when user copies the link
        try {
            if (user) {
                await statsService.incrementShares(articleId, user.uid, {
                    cachedUserData: userDataCacheRef.current,
                    cachedPublishedDoc: publishedDocRef.current
                });
                // Real-time listener will update the stats automatically
            }
        } catch (error) {
            console.error('LearnArticle: Error incrementing share count:', error);
        }
        
        const articleUrl = getArticleUrl();
        try {
            await navigator.clipboard.writeText(articleUrl);
            setCopyButtonText("Copied!");
            setTimeout(() => setCopyButtonText("Copy Link"), 2000);
        } catch (err) {
            console.error('Failed to copy link:', err);
        }
    };

    const handleSocialShare = async (platform) => {
        const articleId = article.id;
        const user = auth.currentUser;

        // Increment shares only when user actually clicks a platform button
        try {
            if (user) {
                await statsService.incrementShares(articleId, user.uid, {
                    cachedUserData: userDataCacheRef.current,
                    cachedPublishedDoc: publishedDocRef.current
                });
                // Real-time listener will update the stats automatically
            }
        } catch (error) {
            console.error('LearnArticle: Error incrementing share count:', error);
        }
        
        const articleUrl = getArticleUrl();
        const url = encodeURIComponent(articleUrl);
        const title = encodeURIComponent(article.title || "");
        const description = encodeURIComponent(article.description || "");

        let shareUrl = "";

        switch (platform) {
            case "facebook":
                shareUrl = `https://www.facebook.com/sharer/sharer.php?u=${url}`;
                break;
            case "messenger":
                const facebookAppId = process.env.REACT_APP_FACEBOOK_APP_ID;
                if (facebookAppId) {
                    const redirectUri = encodeURIComponent(window.location.origin);
                    shareUrl = `https://www.facebook.com/dialog/send?link=${url}&app_id=${facebookAppId}&redirect_uri=${redirectUri}&display=popup`;
                    window.open(shareUrl, "_blank", "width=600,height=500,noopener,noreferrer");
                    return;
                } else if (navigator.share) {
                    navigator.share({
                        title: article.title || "",
                        text: article.description || "",
                        url: articleUrl
                    }).catch(err => {
                        console.error('Navigator share failed:', err);
                        fallbackShare(articleUrl, article.title);
                    });
                    return;
                } else {
                    fallbackShare(articleUrl, article.title);
                    return;
                }
            case "twitter":
                shareUrl = `https://twitter.com/intent/tweet?url=${url}&text=${title}`;
                break;
            case "whatsapp":
                shareUrl = `https://api.whatsapp.com/send?text=${title}%20${url}`;
                break;
            case "telegram":
                shareUrl = `https://t.me/share/url?url=${url}&text=${title}`;
                break;
            default:
                return;
        }

        if (shareUrl) {
            window.open(shareUrl, "_blank", "noopener,noreferrer");
        }
    };

    const fallbackShare = (articleUrl, title) => {
        const copyLink = window.confirm("Messenger share not available. Would you like to copy the article link instead?");
        if (copyLink) {
            handleCopyLink();
        }
    };

    const handleTextToSpeech = () => {
        if (!article) return;

        if (isPlaying) {
            // Stop speaking (pause)
            if (speechRef.current) {
                // Remove onend listener to prevent hiding during pause
                speechRef.current.onend = null;
                speechRef.current.onerror = null;
                window.speechSynthesis.cancel();
                speechRef.current = null;
            }
            if (speechIntervalRef.current) {
                clearInterval(speechIntervalRef.current);
                speechIntervalRef.current = null;
            }
            setIsPlaying(false);
            // Don't hide the speech control bar when pausing - keep it visible for resume
            setShowSpeechControl(true);
        } else {
            // Start new speech
            // Extract text from bodyHtml content
            const textContent = backendBodyHtml ? backendBodyHtml.replace(/<[^>]+>/g, '') : '';
            const fullText = `${article.title}. ${textContent}`;
            
            // Estimate duration (rough calculation: average reading speed is 200 words per minute)
            const wordCount = fullText.split(' ').length;
            const estimatedDuration = (wordCount / 200) * 60; // Convert to seconds
            setSpeechDuration(estimatedDuration);
            setSpeechCurrentTime(0);
            
            if ('speechSynthesis' in window) {
                const utterance = new SpeechSynthesisUtterance(fullText);
                utterance.rate = playbackRate; // Apply playback rate directly
                utterance.pitch = 1;
                utterance.volume = 1;
                
                utterance.onstart = () => {
                    setIsPlaying(true);
                    setShowSpeechControl(true);
                    speechStartTimeRef.current = Date.now();
                    
                    // Clear any existing interval
                    if (speechIntervalRef.current) {
                        clearInterval(speechIntervalRef.current);
                        speechIntervalRef.current = null;
                    }
                };
                
                utterance.onend = () => {
                    setIsPlaying(false);
                    setShowSpeechControl(false);
                    speechRef.current = null;
                    if (speechIntervalRef.current) {
                        clearInterval(speechIntervalRef.current);
                        speechIntervalRef.current = null;
                    }
                    // Update final duration based on actual speech time
                    const actualDuration = (Date.now() - speechStartTimeRef.current) / 1000;
                    setSpeechDuration(actualDuration);
                    setSpeechCurrentTime(actualDuration);
                    speechStartTimeRef.current = 0;
                };
                
                utterance.onerror = () => {
                    setIsPlaying(false);
                    setShowSpeechControl(false);
                    speechRef.current = null;
                    if (speechIntervalRef.current) {
                        clearInterval(speechIntervalRef.current);
                        speechIntervalRef.current = null;
                    }
                };
                
                // Use speech boundary events for accurate progress tracking
                utterance.onboundary = (event) => {
                    if (event.name === 'word') {
                        // Calculate progress based on word boundaries
                        const elapsed = (Date.now() - speechStartTimeRef.current) / 1000;
                        setSpeechCurrentTime(elapsed);
                    }
                };
                
                speechRef.current = utterance;
                window.speechSynthesis.speak(utterance);
            } else {
                console.error('Speech synthesis not supported');
            }
        }
    };

    const handleSpeechSeek = (percentage) => {
        if (!article) return;
        
        const wasPlaying = isPlaying; // Store current playing state
        const newTime = (percentage / 100) * speechDuration;
        const newPercentage = percentage;
        
        // Update seek bar position immediately for responsive UI
        setSpeechCurrentTime(newTime);
        seekPositionRef.current = newPercentage;
        
        // Always show the speech control when seeking
        setShowSpeechControl(true);
        
        // Stop current speech if playing (without triggering onend that hides control)
        if (speechRef.current) {
            // Remove onend listener to prevent hiding during seek
            speechRef.current.onend = null;
            speechRef.current.onerror = null;
            window.speechSynthesis.cancel();
            speechRef.current = null;
        }
        if (speechIntervalRef.current) {
            clearInterval(speechIntervalRef.current);
            speechIntervalRef.current = null;
        }
        
        // Extract text from bodyHtml content
        const textContent = backendBodyHtml ? backendBodyHtml.replace(/<[^>]+>/g, '') : '';
        const fullText = `${article.title}. ${textContent}`;
        const words = fullText.split(' ');
        const totalWords = words.length;
        const wordsToSkip = Math.floor((percentage / 100) * totalWords);
        const remainingText = words.slice(wordsToSkip).join(' ');
        
        if ('speechSynthesis' in window && remainingText) {
            const utterance = new SpeechSynthesisUtterance(remainingText);
            utterance.rate = playbackRate;
            utterance.pitch = 1;
            utterance.volume = 1;
            
            utterance.onstart = () => {
                // Set playing state if it was playing before seek
                if (wasPlaying) {
                    setIsPlaying(true);
                }
                // Adjust start time to account for seek position
                speechStartTimeRef.current = Date.now() - (newTime * 1000);
            };
            
            // Use speech boundary events for accurate progress tracking
            utterance.onboundary = (event) => {
                if (event.name === 'word' && wasPlaying) {
                    // Calculate progress based on word boundaries
                    const elapsed = (Date.now() - speechStartTimeRef.current) / 1000;
                    const currentProgress = (elapsed / speechDuration) * 100;
                    
                    // Update seek bar position
                    setSpeechCurrentTime(elapsed);
                    seekPositionRef.current = currentProgress;
                }
            };
            
            utterance.onend = () => {
                setIsPlaying(false);
                setShowSpeechControl(false);
                speechRef.current = null;
                if (speechIntervalRef.current) {
                    clearInterval(speechIntervalRef.current);
                    speechIntervalRef.current = null;
                }
                // Update final duration based on actual speech time
                const actualDuration = (Date.now() - speechStartTimeRef.current) / 1000;
                setSpeechDuration(actualDuration);
                setSpeechCurrentTime(actualDuration);
                seekPositionRef.current = 100;
                speechStartTimeRef.current = 0;
            };
            
            utterance.onerror = () => {
                setIsPlaying(false);
                setShowSpeechControl(false);
                speechRef.current = null;
                if (speechIntervalRef.current) {
                    clearInterval(speechIntervalRef.current);
                    speechIntervalRef.current = null;
                }
                speechStartTimeRef.current = 0;
            };
            
            speechRef.current = utterance;
            
            // Start speaking if it was playing before seek
            if (wasPlaying) {
                window.speechSynthesis.speak(utterance);
            } else {
                // If not playing, just prepare the utterance for later
                setIsPlaying(false);
            }
        }
    };

    const handleCloseSpeechControl = () => {
        // Stop speech synthesis immediately
        if (speechRef.current) {
            window.speechSynthesis.cancel();
            speechRef.current = null;
        }
        if (speechIntervalRef.current) {
            clearInterval(speechIntervalRef.current);
            speechIntervalRef.current = null;
        }
        
        // Reset speech state
        setIsPlaying(false);
        setShowSpeechControl(false);
        setSpeechCurrentTime(0);
        speechStartTimeRef.current = 0;
    };

    const handlePlaybackRateChange = () => {
        const rates = [1, 1.5, 2];
        const currentIndex = rates.indexOf(playbackRate);
        const nextIndex = (currentIndex + 1) % rates.length;
        const newRate = rates[nextIndex];
        setPlaybackRate(newRate);

        // Recalculate duration based on new playback rate
        const textContent = backendBodyHtml ? backendBodyHtml.replace(/<[^>]+>/g, '') : '';
        const fullText = `${article.title}. ${textContent}`;
        const wordCount = fullText.split(' ').length;
        const baseDuration = (wordCount / 200) * 60; // Base duration at 1x speed
        const newDuration = baseDuration / newRate; // Adjust duration based on playback rate
        setSpeechDuration(newDuration);

        // Adjust current time proportionally to maintain position in content
        const currentTimeRatio = speechCurrentTime / (speechDuration || 1);
        const newCurrentTime = currentTimeRatio * newDuration;
        setSpeechCurrentTime(newCurrentTime);

        // Update speech synthesis rate if currently speaking
        if ('speechSynthesis' in window && window.speechSynthesis.speaking && speechRef.current) {
            const currentText = speechRef.current.text;

            // Remove onend listener to prevent hiding during speed change
            speechRef.current.onend = null;
            speechRef.current.onerror = null;
            window.speechSynthesis.cancel();

            const utterance = new SpeechSynthesisUtterance(currentText);
            utterance.rate = newRate;
            utterance.pitch = 1;
            utterance.volume = 1;

            utterance.onstart = () => {
                setIsPlaying(true);
                setShowSpeechControl(true);
                speechStartTimeRef.current = Date.now() - (newCurrentTime * 1000);
            };

            utterance.onboundary = (event) => {
                if (event.name === 'word') {
                    // Calculate progress based on word boundaries
                    const elapsed = (Date.now() - speechStartTimeRef.current) / 1000;
                    setSpeechCurrentTime(elapsed);
                }
            };

            utterance.onend = () => {
                setIsPlaying(false);
                setShowSpeechControl(false);
                speechRef.current = null;
                if (speechIntervalRef.current) {
                    clearInterval(speechIntervalRef.current);
                    speechIntervalRef.current = null;
                }
                // Update final duration based on actual speech time
                const actualDuration = (Date.now() - speechStartTimeRef.current) / 1000;
                setSpeechDuration(actualDuration);
                setSpeechCurrentTime(actualDuration);
                speechStartTimeRef.current = 0;
            };

            utterance.onerror = () => {
                setIsPlaying(false);
                setShowSpeechControl(false);
                speechRef.current = null;
                if (speechIntervalRef.current) {
                    clearInterval(speechIntervalRef.current);
                    speechIntervalRef.current = null;
                }
                speechStartTimeRef.current = 0;
            };
            
            speechRef.current = utterance;
            window.speechSynthesis.speak(utterance);
        }
    };

    // Helper function to add spacing to HTML content
    const addParagraphSpacing = (html) => {
        if (!html) return html;
        // Add margin to all paragraph tags
        let processedHtml = html.replace(/<p([^>]*)>/g, '<p$1 style="margin-bottom: 12px; margin-top: 0; line-height: 1.8; padding-bottom: 8px;">');
        // Add margin to div tags that might contain paragraphs
        processedHtml = processedHtml.replace(/<div([^>]*)>/g, '<div$1 style="margin-bottom: 12px;">');
        // Add margin to heading tags
        processedHtml = processedHtml.replace(/<h([1-6])([^>]*)>/g, '<h$1$2 style="margin-bottom: 16px; margin-top: 24px;">');
        // Add margin to list tags
        processedHtml = processedHtml.replace(/<(ul|ol)([^>]*)>/g, '<$1$2 style="margin-bottom: 24px;">');
        // Add margin to blockquote
        processedHtml = processedHtml.replace(/<blockquote([^>]*)>/g, '<blockquote$1 style="margin-bottom: 24px;">');
        return processedHtml;
    };

    const handleDownloadPDF = () => {
        const printWindow = window.open('', '_blank');
        if (!printWindow || !article) return;

        const safeTitle = (article.title || "AuraVit Article").replace(/"/g, '&quot;');
        const generatedOn = new Date().toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" });
        const generatedTime = new Date().toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit" });
        const readTime = getArticleReadTime();
        const articleDate = getArticleDate();
        const categoryLabel = (article.category || "Wellness")
            .replace(/([A-Z])/g, ' $1')
            .replace(/^./, s => s.toUpperCase())
            .trim();
        const safeBody = backendBodyHtml || "";
        const heroImg = article.img || "";

        const buildDocument = (logoHtml) => `
    <!DOCTYPE html>
    <html>
    <head>
    <meta charset="utf-8" />
    <title>${safeTitle}</title>
    <style>
  @page {
    size: A4 portrait;
    margin: 0;
  }

  * { box-sizing: border-box; }

  html, body {
    margin: 0;
    padding: 0;
    background: #ffffff;
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;
    font-family: Arial, sans-serif;
    color: #1e293b;
    text-rendering: optimizeLegibility;
    -webkit-font-smoothing: antialiased;
    width: 210mm;
    overflow: visible;
  }

  body { font-size: 10.5pt; line-height: 1.6; }

  /* =========================================================
     MEASUREMENT CONTAINER — hidden, used during layout pass
     ========================================================= */
  #measure {
    width: 210mm;
    padding: 22mm 18mm 22mm 18mm;
    position: absolute;
    left: -10000px;
    top: 0;
    visibility: hidden;
    overflow: visible;
  }

  /* =========================================================
     PER-PAGE WRAPPER — each page is exactly one A4 sheet
     Header absolute top, footer absolute bottom, content padded
     ========================================================= */
  .page {
    position: relative;
    width: 210mm;
    height: 297mm;
    overflow: hidden;
    page-break-after: always;
    page-break-inside: avoid;
    break-after: page;
    break-inside: avoid-page;
  }
  .page:last-child {
    page-break-after: auto;
    break-after: auto;
  }
  .page-content {
    position: absolute;
    top: 22mm;
    bottom: 40mm;
    left: 18mm;
    right: 18mm;
    overflow: hidden;
    height: auto;
    max-height: 235mm;
    padding-bottom: 0;
    word-wrap: break-word;
    overflow-wrap: break-word;
  }

  /* =========================================================
     HEADER (per-page absolute, 20mm tall)
     ========================================================= */
  .doc-header {
    position: absolute;
    top: 0;
    left: 0;
    width: 210mm;
    height: 20mm;
    background: #f5f8ff;
    padding: 3.5mm 18mm 2mm 18mm;
    overflow: visible;
  }
  .doc-header::after {
    content: "";
    position: absolute;
    left: 18mm;
    right: 18mm;
    bottom: 0;
    height: 1px;
    background: #dbeafe;
  }
  .doc-header-inner {
    display: flex;
    align-items: center;
    justify-content: space-between;
    height: 100%;
  }
  .doc-brand { display: flex; align-items: center; gap: 8px; }
  .doc-brand img, .doc-brand .brand-mark { display: block; }
  .doc-brand img { height: 10mm; width: auto; max-width: 35mm; object-fit: contain; }
  .brand-mark {
    width: 8.5mm; height: 8.5mm;
    border-radius: 5px;
    background: linear-gradient(135deg, #3b82f6, #1e3a8a);
    color: #fff;
    font-weight: 800; font-size: 11pt;
    display: flex; align-items: center; justify-content: center;
  }
  .doc-brand-text { display: flex; flex-direction: column; line-height: 1.15; }
  .doc-brand-title { font-size: 11.5pt; font-weight: 700; color: #1e40af; letter-spacing: 0.1px; }
  .doc-brand-tag { font-size: 7.5pt; color: #64748b; margin-top: 1.5px; font-weight: 500; }
  .doc-header-right { text-align: right; font-size: 7.8pt; color: #64748b; line-height: 1.55; }
  .doc-header-right .label { display: block; color: #94a3b8; font-size: 6.6pt; text-transform: uppercase; letter-spacing: 0.6px; font-weight: 600; }

  /* =========================================================
     FOOTER (per-page absolute, 22mm tall)
     ========================================================= */
  .doc-footer {
    position: absolute;
    bottom: 0;
    left: 0;
    width: 210mm;
    height: 22mm;
    background: #f5f8ff;
    padding: 2.5mm 18mm 3.5mm 18mm;
    overflow: visible;
    z-index: 1000;
  }
  .doc-footer::before {
    content: "";
    position: absolute;
    left: 18mm;
    right: 18mm;
    top: 0;
    height: 1px;
    background: #dbeafe;
  }
  .doc-footer-inner { display: flex; align-items: center; justify-content: space-between; height: 100%; }
  .doc-footer-left { display: flex; flex-direction: column; line-height: 1.3; }
  .doc-footer-brand { font-size: 9pt; font-weight: 700; color: #1e40af; }
  .doc-footer-sub { font-size: 7.3pt; color: #64748b; margin-top: 1px; }
  .doc-footer-right {
    text-align: right;
    font-size: 8pt;
    color: #475569;
    font-weight: 600;
    font-variant-numeric: tabular-nums;
    line-height: 1.4;
  }
  .doc-footer-right .pages-label { display: block; font-size: 6.6pt; color: #94a3b8; text-transform: uppercase; letter-spacing: 0.5px; font-weight: 600; margin-bottom: 1px; }

  /* =========================================================
     TITLE SECTION (inside page-content, flow)
     ========================================================= */
  .article-category {
    display: inline-block;
    padding: 2.2pt 9pt;
    border-radius: 999px;
    background: #eef2ff;
    color: #3730a3;
    font-weight: 700;
    font-size: 8pt;
    letter-spacing: 0.3px;
    margin: 0 0 5pt 0;
    text-transform: uppercase;
  }
  h1.article-title {
    color: #0f172a;
    font-size: 20pt;
    font-weight: 700;
    line-height: 1.22;
    letter-spacing: -0.01em;
    margin: 0 0 6pt 0;
    orphans: 3; widows: 3;
  }
  .article-meta {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 4pt 9pt;
    color: #64748b;
    font-size: 9pt;
    padding: 0 0 10pt 0;
    margin: 0 0 10pt 0;
    border-bottom: 1px solid #e0e7ff;
  }
  .meta-sep { color: #cbd5e1; }

  img.hero-image {
    width: 100%;
    max-width: 100%;
    height: auto;
    max-height: 92mm;
    object-fit: contain;
    display: block;
    border-radius: 7px;
    border: 1px solid #e2e8f0;
    box-shadow: 0 2px 10px rgba(30, 58, 138, 0.06);
    margin: 0 0 12pt 0;
    page-break-inside: avoid;
    break-inside: avoid-page;
    box-decoration-break: clone;
    overflow: visible;
    position: relative;
  }

  /* =========================================================
     ARTICLE BODY
     ========================================================= */
  .article-body { margin-top: 12pt; overflow: visible; word-wrap: break-word; overflow-wrap: break-word; }

  .article-body p {
    margin: 0 0 16pt 0;
    orphans: 3; widows: 3;
    text-align: left;
    hyphens: auto;
    overflow: visible;
    page-break-inside: auto;
    break-inside: auto;
    word-wrap: break-word;
    overflow-wrap: break-word;
    max-width: 100%;
  }

  h2, h3, h4 {
    color: #1e3a8a;
    font-weight: 700;
    line-height: 1.3;
    page-break-after: auto;
    break-after: auto;
    page-break-inside: auto;
    break-inside: auto;
    orphans: 3; widows: 3;
  }
  h2 { font-size: 14pt; margin: 18pt 0 5pt 0; padding-bottom: 3.5pt; border-bottom: 1px solid #eef2ff; }
  h3 { font-size: 12pt; margin: 13pt 0 4pt 0; }
  h4 { font-size: 10.8pt; margin: 10pt 0 3pt 0; }

  .article-body ul,
  .article-body ol {
    margin: 0 0 9pt 18pt;
    padding: 0;
    page-break-inside: avoid;
    break-inside: avoid-page;
  }
  .article-body li {
    margin-bottom: 3.5pt;
    orphans: 3; widows: 3;
    line-height: 1.55;
  }

  .article-body strong, .article-body b { color: #0f172a; font-weight: 700; }
  .article-body em, .article-body i { color: #334155; }

  .article-body img {
    max-width: 100%; height: auto; display: block;
    margin: 9pt auto;
    border-radius: 5px;
    border: 1px solid #e2e8f0;
    page-break-inside: avoid;
    break-inside: avoid-page;
  }

  .article-body a { color: #1d4ed8; text-decoration: none; word-break: break-word; }

  .article-body blockquote {
    margin: 11pt 0;
    padding: 8pt 12pt;
    border-left: 3px solid #c7d2fe;
    background: #eef2ff;
    color: #312e81;
    font-size: 10pt;
    page-break-inside: avoid;
    break-inside: avoid-page;
    border-radius: 0 5px 5px 0;
    line-height: 1.55;
  }

  .article-body hr {
    border: none;
    border-top: 1px solid #e2e8f0;
    margin: 11pt 0;
    page-break-after: avoid;
    break-after: avoid-page;
  }

  .article-body code, .article-body pre {
    font-family: Consolas, "Courier New", monospace;
    font-size: 9.2pt;
    background: #f1f5f9;
    padding: 1.5pt 4pt;
    border-radius: 3px;
    color: #334155;
  }
  .article-body pre {
    padding: 6pt 8pt;
    white-space: pre-wrap;
    page-break-inside: avoid;
    break-inside: avoid-page;
  }

  .article-body table {
    width: 100%; border-collapse: collapse;
    margin: 9pt 0; font-size: 9.5pt;
    page-break-inside: avoid;
    break-inside: avoid-page;
  }
  .article-body table th, .article-body table td { border: 1px solid #e2e8f0; padding: 5pt 7pt; text-align: left; }
  .article-body table th { background: #eef2ff; color: #1e3a8a; font-weight: 700; }

  /* =========================================================
     SMART KEEP-TOGETHER UNITS — prevent orphans
     ========================================================= */
  .keep-together {
    page-break-inside: auto;
    break-inside: auto;
    orphans: 3;
    widows: 3;
    overflow: visible;
  }
  .keep-together p:last-child { margin-bottom: 0; }
  .keep-together h2, .keep-together h3, .keep-together h4 { margin-top: 0; }

  .intro-pair {
    page-break-inside: auto;
    break-inside: auto;
    overflow: visible;
  }
  .intro-pair p { margin-bottom: 4pt; }
  .intro-pair ul, .intro-pair ol { margin-top: 0; }

  /* Allow article body to break naturally across pages */
  .article-body {
    page-break-inside: auto;
    break-inside: auto;
    overflow: visible;
    word-wrap: break-word;
    overflow-wrap: break-word;
  }
</style>
</head>
<body>

<!-- ====== MEASUREMENT TEMPLATE (hidden; JS will build pages from its children) ====== -->
<div id="measure">
  <div data-block="category" class="article-category keep-together">${categoryLabel}</div>
  <h1 data-block="title" class="article-title keep-together">${safeTitle}</h1>
  <div data-block="meta" class="article-meta keep-together">
    <span>${readTime} min read</span>
    <span class="meta-sep">·</span>
    <span>Published ${articleDate}</span>
  </div>
  ${heroImg ? `<div data-block="hero" class="keep-together"><img src="${heroImg}" alt="${safeTitle}" class="hero-image" /></div>` : ''}
  <div data-block="body" class="article-body" id="raw-body">${safeBody}</div>
</div>

<!-- ====== PAGINATED OUTPUT — JS will populate <body> with .page elements ====== -->

<script>
(function(){
  var HEADER_MM = 20;
  var FOOTER_MM = 22;
  var CONTENT_BOTTOM_MM = 40;
  var GAP_MM    = 2;
  var PAGE_W_MM = 210;
  var PAGE_H_MM = 297;
  var LR_MARGIN_MM = 18;
  var CONTENT_H_MM = PAGE_H_MM - (HEADER_MM + GAP_MM) - CONTENT_BOTTOM_MM;
  var MM_PER_PX = 25.4 / 96;
  var CONTENT_H_PX = CONTENT_H_MM / MM_PER_PX;

  var LOGO_HTML = ${JSON.stringify(logoHtml)};
  var GEN_DATE = ${JSON.stringify(generatedOn)};
  var GEN_TIME = ${JSON.stringify(generatedTime)};

  function buildHeader() {
    return (
      '<header class="doc-header">' +
        '<div class="doc-header-inner">' +
          '<div class="doc-brand">' +
            LOGO_HTML +
          '</div>' +
          '<div class="doc-header-right">' +
            '<span class="label">Downloaded for you</span>' +
            GEN_DATE + ' · ' + GEN_TIME +
          '</div>' +
        '</div>' +
      '</header>'
    );
  }

  function buildFooter(pageNum, totalPages) {
    return (
      '<footer class="doc-footer">' +
        '<div class="doc-footer-inner">' +
          '<div class="doc-footer-left">' +
            '<div class="doc-footer-brand">AuraVit-CFSI</div>' +
            '<div class="doc-footer-sub">Health Education · Learn Module · For personal use only</div>' +
          '</div>' +
          '<div class="doc-footer-right">' +
            '<span class="pages-label">Page</span>' +
            'Page ' + pageNum + ' of ' + totalPages +
          '</div>' +
        '</div>' +
      '</footer>'
    );
  }

  function buildPage(contentHtml, pageNum, totalPages) {
    return (
      '<section class="page">' +
        buildHeader() +
        '<div class="page-content">' + contentHtml + '</div>' +
        buildFooter(pageNum, totalPages) +
      '</section>'
    );
  }

  /**
   * Convert the #raw-body rich content into a flat list of logical blocks.
   * Also: wrap heading+paragraph(s), intro-p+list into .keep-together / .intro-pair wrappers.
   */
  function extractBlocks() {
    var blocks = [];
    var measure = document.getElementById('measure');
    var topChildren = measure.children;
    // Process category, title, meta, hero from top level first
    for (var i = 0; i < topChildren.length; i++) {
      var el = topChildren[i];
      if (el.getAttribute('data-block') === 'body') continue;
      blocks.push(el);
    }

    // Now process article-body children; group intro-p followed by list, and heading + following p
    var rawBody = document.getElementById('raw-body');
    if (rawBody) {
      var bodyChildren = [];
      // Convert HTMLCollection to array so we can manipulate
      for (var k = 0; k < rawBody.children.length; k++) bodyChildren.push(rawBody.children[k]);

      var idx = 0;
      while (idx < bodyChildren.length) {
        var node = bodyChildren[idx];
        var tag = (node.tagName || '').toLowerCase();

        // Heading followed by paragraph(s) / content: keep heading + first following block together
        if (tag === 'h2' || tag === 'h3' || tag === 'h4') {
          var wrap = document.createElement('div');
          wrap.className = 'keep-together';
          wrap.appendChild(node.cloneNode(true));
          // Attach the next sibling if it's p/ul/ol/blockquote/etc. (non-heading)
          if (idx + 1 < bodyChildren.length) {
            var nextTag = (bodyChildren[idx+1].tagName || '').toLowerCase();
            if (nextTag !== 'h2' && nextTag !== 'h3' && nextTag !== 'h4') {
              wrap.appendChild(bodyChildren[idx+1].cloneNode(true));
              idx++;
            }
          }
          blocks.push(wrap);
          idx++;
          continue;
        }

        // Intro paragraph immediately before a list: keep pair together
        if (tag === 'p' && idx + 1 < bodyChildren.length) {
          var nxt = bodyChildren[idx+1];
          var nxtTag = (nxt.tagName || '').toLowerCase();
          if (nxtTag === 'ul' || nxtTag === 'ol') {
            var pair = document.createElement('div');
            pair.className = 'intro-pair';
            pair.appendChild(node.cloneNode(true));
            pair.appendChild(nxt.cloneNode(true));
            blocks.push(pair);
            idx += 2;
            continue;
          }
        }

        // Everything else: standalone block
        blocks.push(node.cloneNode(true));
        idx++;
      }
    }

    return blocks;
  }

  /**
   * Measure rendered height of a node in pixels.
   * Requires the node to be attached inside the (wide, hidden) #measure container.
   */
  function measureHeight(node) {
    var stage = document.getElementById('measure');
    // Clear previous measure staging (keep template children? simpler: append, measure, remove)
    var wrapper = document.createElement('div');
    wrapper.style.width = (PAGE_W_MM - LR_MARGIN_MM*2) + 'mm';
    wrapper.style.overflow = 'visible';
    wrapper.appendChild(node);
    stage.appendChild(wrapper);
    var h = wrapper.offsetHeight || wrapper.scrollHeight || 0;
    // Clean up
    stage.removeChild(wrapper);
    return h;
  }

  /**
   * Greedy bin-pack: assign blocks to pages, keeping each block atomic.
   * If a block alone exceeds a page, it stays alone (its overflow will paginate naturally
   * via browser; the block is big so splitting inside it is acceptable).
   * Updated to be much more conservative to prevent footer overlap.
   */
  function paginate(blocks, heights) {
    var pages = [[]];
    var remaining = CONTENT_H_PX;
    var SAFETY_MARGIN_PX = 50; // Extra safety margin to prevent footer overlap
    for (var i = 0; i < blocks.length; i++) {
      var h = heights[i];
      // If block exceeds remaining space (with safety margin) and page has content, move to next page
      if (h > remaining - SAFETY_MARGIN_PX && pages[pages.length-1].length > 0) {
        pages.push([]);
        remaining = CONTENT_H_PX;
      }
      // If block is larger than a full page (with safety margin), put it on its own page
      if (h > CONTENT_H_PX - SAFETY_MARGIN_PX && pages[pages.length-1].length > 0) {
        pages.push([]);
        remaining = CONTENT_H_PX;
      }
      pages[pages.length-1].push(i);
      remaining -= h;
      // If we go significantly negative, force a page break to prevent content from running into footer
      if (remaining < -SAFETY_MARGIN_PX) {
        pages.push([]);
        remaining = CONTENT_H_PX;
      }
    }
    return pages;
  }

  function run() {
    try {
      var blocks = extractBlocks();
      // Measure each block (clones; original #measure kept as cache for reuse)
      var heights = [];
      for (var i = 0; i < blocks.length; i++) {
        var h = measureHeight(blocks[i]);
        heights.push(h);
      }

      var pageAssignments = paginate(blocks, heights);
      var totalPages = pageAssignments.length;

      var bodyHtml = '';
      for (var p = 0; p < totalPages; p++) {
        var content = '';
        var idxs = pageAssignments[p];
        for (var k = 0; k < idxs.length; k++) {
          var b = blocks[idxs[k]];
          // Use outerHTML; for document-fragment-like nodes we appended above,
          // blocks[i] is either the original element (from topChildren) or a clone wrapper.
          // Both have outerHTML.
          content += b.outerHTML || '';
        }
        bodyHtml += buildPage(content, p + 1, totalPages);
      }

      // Clear body, remove hidden measure, inject paginated pages
      document.body.innerHTML = bodyHtml;
    } catch (err) {
      // Fallback: just expose hidden measure as content
      var m = document.getElementById('measure');
      if (m) { m.style.position = 'static'; m.style.left = '0'; m.style.visibility = 'visible'; }
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function(){ run(); window.__layoutReady = true; });
  } else {
    run();
    window.__layoutReady = true;
  }

  try {
    // Only run window-close logic inside the isolated popup, NEVER on opener/main window.
    // Guard: confirm this window has no opener reference, or it is itself a popup window.
    var isPopup = (window.opener && window.opener !== window) || window.name === '';
    if (isPopup) {
      window.onafterprint = function(){
        try { window.close(); } catch(e) {}
      };
    }
  } catch(e) {}
})();
</script>
</body>
</html>`;

        const triggerPrint = () => {
          printWindow.onload = function() {
            var attempts = 0;
            var tryPrint = function() {
              attempts++;
              var ready = false;
              try { ready = printWindow.__layoutReady === true; } catch(e) {}
              if (ready || attempts >= 20) {
                try { printWindow.focus(); } catch(e) {}
                try { printWindow.print(); } catch(e) {}
              } else {
                setTimeout(tryPrint, 80);
              }
            };
            setTimeout(tryPrint, 200);
          };
        };

        const logoReader = new FileReader();
        let resolvedFallback = false;

        const writeFallbackDoc = () => {
          if (resolvedFallback) return;
          resolvedFallback = true;
          const logoHtml = `<div class="brand-mark">A</div>`;
          printWindow.document.open();
          printWindow.document.write(buildDocument(logoHtml));
          printWindow.document.close();
          triggerPrint();
        };

        logoReader.onload = function(e) {
          if (resolvedFallback) return;
          resolvedFallback = true;
          const logoDataUrl = e.target.result;
          const logoHtml = `<img src="${logoDataUrl}" alt="AuraVit-CFSI" />`;
          printWindow.document.open();
          printWindow.document.write(buildDocument(logoHtml));
          printWindow.document.close();
          triggerPrint();
        };
        logoReader.onerror = writeFallbackDoc;

        fetch(pdfLogo)
          .then(response => {
            if (!response.ok) throw new Error('logo fetch failed');
            return response.blob();
          })
          .then(blob => logoReader.readAsDataURL(blob))
          .catch(error => {
            console.error('Error loading logo:', error);
            writeFallbackDoc();
          });

        // Safety timeout: if neither resolve nor error fires within 4s, use fallback
        setTimeout(() => { if (!resolvedFallback) writeFallbackDoc(); }, 4000);
    };

    if (isLoading) {
        return (
            <div className="article-page-container">
                <div className="article-page-header">
                    <div className="article-header-left">
                        <button className="article-back-button" onClick={handleBack}>
                            <FiArrowLeft />
                        </button>
                        <div className="article-logos">
                            <img src={logoAuraVit} alt="AuraVit" className="article-logo auravit" />
                            <span className="logo-separator">|</span>
                            <img src={cfsiLogo} alt="CFSI" className="article-logo cfsi" />
                        </div>
                    </div>
                    <div className="article-header-right">
                    </div>
                </div>
                <div className="article-page-content-wrapper">
                    <div className="article-page-content">
                        {/* Skeleton Category */}
                        <div className="skeleton skeleton-category"></div>
                        
                        {/* Skeleton Title */}
                        <div className="skeleton skeleton-title"></div>
                        <div className="skeleton skeleton-title-short"></div>
                        
                        {/* Skeleton Engagement Metrics */}
                        <div className="article-page-engagement-metrics">
                            <div className="skeleton skeleton-metric"></div>
                            <div className="skeleton skeleton-metric"></div>
                            <div className="skeleton skeleton-metric"></div>
                        </div>
                        
                        {/* Skeleton Meta */}
                        <div className="article-page-meta">
                            <div className="skeleton skeleton-meta"></div>
                            <div className="skeleton skeleton-meta"></div>
                        </div>
                        
                        {/* Skeleton Image */}
                        <div className="skeleton skeleton-image"></div>
                        
                        {/* Skeleton Body */}
                        <div className="skeleton skeleton-body"></div>
                        <div className="skeleton skeleton-body"></div>
                        <div className="skeleton skeleton-body"></div>
                        <div className="skeleton skeleton-body-short"></div>
                    </div>
                    
                    {/* Skeleton Related Articles */}
                    <div className="article-page-related-articles">
                        <div className="skeleton skeleton-related-header"></div>
                        <div className="skeleton skeleton-related-item"></div>
                        <div className="skeleton skeleton-related-item"></div>
                        <div className="skeleton skeleton-related-item"></div>
                    </div>
                </div>
            </div>
        );
    }

    if (articleNotFound) {
        return (
            <div className="article-page-container">
                <div className="article-page-header">
                    <div className="article-header-left">
                        <button className="article-back-button" onClick={handleBack}>
                            <FiArrowLeft />
                        </button>
                        <div className="article-logos">
                            <img src={logoAuraVit} alt="AuraVit" className="article-logo auravit" />
                            <span className="logo-separator">|</span>
                            <img src={cfsiLogo} alt="CFSI" className="article-logo cfsi" />
                        </div>
                    </div>
                    <div className="article-header-right">
                    </div>
                </div>
                <div className="article-page-content">
                </div>
            </div>
        );
    }

    // Extract text from bodyHtml content
    const textContent = backendBodyHtml ? backendBodyHtml.replace(/<[^>]+>/g, '') : '';
    const content = {
        paragraphs: [
            textContent
        ]
    };

    return (
        <div className="article-page-container">
            {/* Header */}
            <div className="article-page-header">
                <div className="article-header-left">
                    <button className="article-back-button" onClick={handleBack}>
                        <FiArrowLeft />
                    </button>
                    <div className="article-logos">
                        <img src={logoAuraVit} alt="AuraVit" className="article-logo auravit" />
                        <span className="logo-separator">|</span>
                        <img src={cfsiLogo} alt="CFSI" className="article-logo cfsi" />
                    </div>
                </div>
                <div className="article-header-right">
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <button
                            className="article-play-button"
                            onClick={handleTextToSpeech}
                            title={isPlaying ? "Pause reading" : "Read article aloud"}
                        >
                            {isPlaying ? <FiPause /> : <FiPlay />}
                        </button>
                        <div className="article-dropdown-container">
                            <button
                                className="article-font-button"
                                onClick={() => setShowDropdown(!showDropdown)}
                                title="More options"
                            >
                                <FiMoreVertical />
                            </button>
                            {showDropdown && (
                                <div className="article-dropdown-menu">
                                    <button
                                        className="article-dropdown-item"
                                        onClick={() => {
                                        handleBookmark();
                                        setShowDropdown(false);
                                    }}
                                >
                                    <FiBookmark />
                                    {bookmarked ? "Unsave" : "Save"}
                                </button>
                                <button 
                                className="article-dropdown-item"
                                onClick={() => {
                                    setShowFontModal(true);
                                    setShowDropdown(false);
                                }}
                            >
                                <RiFontSize />
                                Font size
                            </button>
                            <button 
                                className="article-dropdown-item"
                                onClick={() => {
                                    handleShare();
                                    setShowDropdown(false);
                                }}
                            >
                                <FiShare2 />
                                Share
                            </button>
                            <button 
                                className="article-dropdown-item"
                                onClick={() => {
                                    handleDownloadPDF();
                                    setShowDropdown(false);
                                }}
                            >
                                <MdFileDownload />
                                Download as PDF
                            </button>
                            </div>
                        )}
                    </div>
                </div>
                </div>
            </div>

            <div className="article-page-content-wrapper">
                {/* Article Content */}
                <div className="article-page-content">
                    {/* Category */}
                    <div className="article-page-category-badge">
                        {article.category.toUpperCase()}
                    </div>

                    {/* Title */}
                    <h1 className="article-page-title">{article.title}</h1>

                    {/* Engagement Metrics */}
                    <div className="article-page-engagement-metrics">
                        <div className="article-page-engagement-metric">
                            <FiEye />
                            <span>{articleStats.views}</span>
                        </div>
                        <div className="article-page-engagement-metric">
                            <FiBookmark />
                            <span>{articleStats.bookmarks}</span>
                        </div>
                        <div className="article-page-engagement-metric">
                            <FiShare2 />
                            <span>{articleStats.shares}</span>
                        </div>
                    </div>

                    {/* Article Meta */}
                    <div className="article-page-meta">
                        <div className="article-page-meta-item">
                            <span>{getArticleReadTime()} min read</span>
                        </div>
                        <div className="article-page-meta-item">
                            <span>{getArticleDate()}</span>
                        </div>
                    </div>

                    {/* Image */}
                    <div className="article-page-image-container">
                        <img src={article.img} alt={article.title} className="article-page-image" />
                    </div>

                    {/* Content */}
                    {backendBodyHtml ? (
                        <>
                        <style>{`
                            .article-page-body p {
                                margin-bottom: 12px !important;
                                margin-top: 0 !important;
                                line-height: 1.8 !important;
                            }
                            .article-page-body p:last-child {
                                margin-bottom: 0 !important;
                            }
                            .article-page-body div {
                                margin-bottom: 24px !important;
                            }
                            .article-page-body div:last-child {
                                margin-bottom: 0 !important;
                            }
                        `}</style>
                        <div
                            className="article-page-body"
                            style={{ fontSize: `${fontSize}px`, lineHeight: '1.8' }}
                            dangerouslySetInnerHTML={{ __html: addParagraphSpacing(backendBodyHtml) }}
                        ></div>
                        </>
                    ) : (
                        <div className="article-page-body" style={{ fontSize: `${fontSize}px` }}>
                            {content.paragraphs.map((paragraph, index) => (
                                <p key={index} className="article-page-paragraph" style={{ marginBottom: '24px' }}>
                                    {paragraph}
                                </p>
                            ))}
                        </div>
                    )}
                </div>

                {/* Related Articles Column */}
                <div className="article-page-related-articles">
                    <h3 className="related-articles-header">Related Articles</h3>
                    <div className="related-articles-list">
                        {relatedArticles.map(relatedArticle => (
                            <div 
                                key={relatedArticle.contentId} 
                                className="related-article-item"
                                onClick={async () => {
                                    const user = auth.currentUser;
                                    if (user) {
                                        try {
                                            const cachedRelated = relatedArticle.contentId
                                                ? publishedDocRef.current
                                                : null;
                                            await statsService.incrementViews(
                                                relatedArticle.contentId,
                                                user.uid,
                                                {
                                                    cachedUserData: userDataCacheRef.current,
                                                    cachedPublishedDoc: cachedRelated
                                                }
                                            );
                                        } catch (err) {
                                            console.error(err);
                                        }
                                    }
                                    navigate(`/learn/article/${relatedArticle.contentId}`);
                                }}
                            >
                                <div className="related-article-content">
                                    <div className="related-article-category-wrapper">
                                        <span className="related-article-category">{relatedArticle.category.toUpperCase()}</span>
                                    </div>
                                    <h4 className="related-article-title">{relatedArticle.title || relatedArticle.socialTitle}</h4>
                                    <p className="related-article-read-time">{calculateReadTime(relatedArticle.bodyHtml)} min read</p>
                                </div>
                                <div className="related-article-image">
                                    <img src={relatedArticle.socialImageUrl} alt={relatedArticle.title || relatedArticle.socialTitle} />
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>

            {/* Share Modal */}
            {shareModal && (
                <div className="article-share-modal-overlay" onClick={handleCloseShareModal}>
                    <div className="article-share-modal" onClick={(e) => e.stopPropagation()}>
                        <div className="article-share-modal-header">
                            <h3 className="article-share-modal-title">Share Article</h3>
                            <button className="article-share-modal-close" onClick={handleCloseShareModal}>
                                <FiX />
                            </button>
                        </div>
                        <div className="article-share-content-preview">
                            <h4 className="article-share-preview-title">{article.title}</h4>
                            <p className="article-share-preview-description">{article.description}</p>
                        </div>
                        <div className="article-share-social-section">
                            <div className="article-share-social-buttons">
                                <button 
                                    className="article-share-social-button article-share-facebook"
                                    onClick={() => handleSocialShare("facebook")}
                                    title="Share on Facebook"
                                >
                                    <FaFacebook />
                                </button>
                                <button 
                                    className="article-share-social-button article-share-twitter"
                                    onClick={() => handleSocialShare("twitter")}
                                    title="Share on X/Twitter"
                                >
                                    <FaXTwitter />
                                </button>
                                <button 
                                    className="article-share-social-button article-share-whatsapp"
                                    onClick={() => handleSocialShare("whatsapp")}
                                    title="Share on WhatsApp"
                                >
                                    <FaWhatsapp />
                                </button>
                                <button 
                                    className="article-share-social-button article-share-telegram"
                                    onClick={() => handleSocialShare("telegram")}
                                    title="Share on Telegram"
                                >
                                    <FaTelegram />
                                </button>
                                <button 
                                    className="article-share-social-button article-share-messenger"
                                    onClick={() => handleSocialShare("messenger")}
                                    title="More Options"
                                >
                                    <FiMoreHorizontal />
                                </button>
                            </div>
                        </div>
                        <div className="article-share-url-section">
                            <label className="article-share-url-label">Article URL:</label>
                            <div className="article-share-url-box">
                                <input
                                    type="text"
                                    className="article-share-url-input"
                                    value={getArticleUrl()}
                                    readOnly
                                />
                                <button 
                                    className={`article-share-copy-button ${copyButtonText === "Copied!" ? "copied" : ""}`}
                                    onClick={handleCopyLink}
                                >
                                    {copyButtonText}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Font Size Modal */}
            {showFontModal && (
                <div className="article-font-modal-overlay" onClick={() => setShowFontModal(false)}>
                    <div className="article-font-modal" onClick={(e) => e.stopPropagation()}>
                        <div className="article-font-modal-header">
                            <h3 className="article-font-modal-title">Font Size</h3>
                            <button className="article-font-modal-close" onClick={() => setShowFontModal(false)}>
                                <FiX />
                            </button>
                        </div>
                        <div className="article-font-content">
                            <div className="font-size-slider-container">
                                <div className="font-size-labels">
                                    <span className="font-size-label">Small</span>
                                    <span className="font-size-label">Medium</span>
                                    <span className="font-size-label">Large</span>
                                </div>
                                <input
                                    type="range"
                                    min="12"
                                    max="24"
                                    value={fontSize}
                                    onChange={(e) => setFontSize(parseInt(e.target.value))}
                                    className="font-size-slider"
                                    step="1"
                                />
                                <div className="font-size-preview">
                                    <span style={{ fontSize: `${fontSize}px` }}>Preview Text</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}



            {/* Speech Control Bar */}
            <LearnSpeechControlBar
                isVisible={showSpeechControl}
                isPlaying={isPlaying}
                articleTitle={article?.title || ''}
                onPlayPause={handleTextToSpeech}
                onClose={handleCloseSpeechControl}
                currentTime={speechCurrentTime}
                duration={speechDuration}
                onSeek={handleSpeechSeek}
                playbackRate={playbackRate}
                onPlaybackRateChange={handlePlaybackRateChange}
            />
        </div>
    );
}
